import { Paper, Stack, Tabs, useMantineTheme } from "@mantine/core";
import { IconCalendarEvent, IconHome, IconUser } from "@tabler/icons-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface BottomNavigationBarProps {
  footerHeight: number;
}

const pathToTabValue = (path: string): string => {
  // The first tab (conceptually "event" or "main home content")
  if (path === "/home/event" || path === "/home" || path === "/") return "event";
  if (path.startsWith("/home/reservation")) return "reservation";
  if (path.startsWith("/home/profile")) return "profile";
  return "event"; // Default to the first tab if no other match
};

export function BottomNavigationBar({ footerHeight }: BottomNavigationBarProps) {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<string>(pathToTabValue(location.pathname));

  const iconColor = (tabValue: string): string => {
    if (activeTab === tabValue) {
      return theme.colors.yellow && theme.colors.yellow[6]
        ? theme.colors.yellow[6]
        : theme.primaryColor;
    }
    return theme.colors.gray[7];
  };

  const handleTabChange = (newTabValue: string | null) => {
    if (newTabValue) {
      setActiveTab(newTabValue);
      switch (newTabValue) {
        case "event":
          navigate("/home");
          break;
        case "reservation":
          navigate("/home/reservation/upcoming");
          break;
        case "profile":
          navigate("/home/profile");
          break;
      }
    }
  };

  return (
    <Paper
      shadow="md"
      radius={0}
      style={{
        borderTop: `1px solid #3a3d41`,
        backgroundColor: theme.black,
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="unstyled"
        style={{ height: `${footerHeight}px` }}
      >
        <Tabs.List grow>
          {/* First tab now has value "event" */}
          <Tabs.Tab value="event" style={{ height: `${footerHeight}px` }}>
            <Stack align="center" gap={2}>
              <IconHome size={26} stroke={1.5} color={iconColor("event")} />
            </Stack>
          </Tabs.Tab>
          <Tabs.Tab value="reservation" style={{ height: `${footerHeight}px` }}>
            <Stack align="center" gap={2}>
              <IconCalendarEvent size={26} stroke={1.5} color={iconColor("reservation")} />
            </Stack>
          </Tabs.Tab>
          <Tabs.Tab value="profile" style={{ height: `${footerHeight}px` }}>
            <Stack align="center" gap={2}>
              <IconUser size={26} stroke={1.5} color={iconColor("profile")} />
            </Stack>
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>
    </Paper>
  );
}
